/**
 * ResultsTabBase
 *
 * Outer shell shared by per-game-type admin Results tabs. Owns:
 *   - the "select a game first" empty state
 *   - the header row (title + Refresh button)
 *   - the outer flex-column layout
 *   - the trailing "Last refreshed" timestamp footer
 *   - the share panel (public results page and Discord post), given the game
 *
 * Game-specific bodies (leaderboard, per-round breakdowns, audit log)
 * render as children. The refresh handler and timestamp are owned by
 * the parent so each game type can pull from its own snapshot context.
 */

import { ReactNode } from 'react';
import type { Game } from '@swtorbit/shared-socket-events';
import { ResultsSharePanel } from './ResultsSharePanel';

export interface ResultsTabBaseProps {
  /** Whether a game is selected — if false, renders the empty state. */
  hasSelectedGame: boolean;
  /** Header title (e.g. "🏁 Results"). */
  title: string;
  /** Optional refresh button click handler — if omitted, no button is rendered. */
  onRefresh?: () => void;
  /** Optional last-refresh epoch ms; rendered as a UTC footer if present. */
  lastRefreshedUtcMs?: number;
  /** Optional override for the empty-state message. */
  emptyMessage?: string;
  /** The game, for the share panel above the results. */
  game?: Game | null;
  children: ReactNode;
}

export function ResultsTabBase({
  hasSelectedGame,
  title,
  onRefresh,
  lastRefreshedUtcMs,
  emptyMessage = 'Select a game to see results.',
  game,
  children,
}: ResultsTabBaseProps) {
  if (!hasSelectedGame) {
    return (
      <div className="bg-space bg-o-50 text-cyan p-sm rounded-base">
        <h2>{title}</h2>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-sm">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="text-white">{title}</h2>
        {onRefresh && (
          <button
            className="btn btn--outline btn--slate"
            onClick={onRefresh}
            title="Refresh snapshot from server"
          >
            🔄 Refresh
          </button>
        )}
      </div>

      {game && <ResultsSharePanel game={game} />}

      {children}

      {lastRefreshedUtcMs !== undefined && (
        <div className="text-slate ff-display fs-sm">
          Last refreshed: {new Date(lastRefreshedUtcMs).toISOString().replace('T', ' ').slice(0, 19)} UTC
        </div>
      )}
    </div>
  );
}
